import { useState, useEffect, useRef } from 'react';
import type {
  FormatPreset,
  ReferenceImage,
  StoryElement,
  StoryElementKind,
  UpdateProjectRequest,
} from '@historyjki/shared';
import {
  FORMAT_PRESETS,
  STYLE_PRESETS,
} from '@historyjki/shared';
import { ensureBookFontsRegistered } from '../../utils/bookFonts';
import { r2Url } from '../../utils/r2Url';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useToast } from '../../hooks/useToast';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { AISuggestion } from '../ui/AISuggestion';
import { Badge } from '../ui/Badge';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { ImageGallery } from '../ui/ImageGallery';
import { ImageEditorList } from '../image-editor';
import { RefineImageModal } from './refine/RefineImageModal';
import { TypographySettings } from '../ui/TypographySettings';
import { CastEditorModal } from './CastEditorModal';
import type { useProject } from '../../hooks/useProject';
import { useDefineStage, type DefineTab } from '../../hooks/useDefineStage';

type ProjectHookReturn = ReturnType<typeof useProject>;

interface DefineStageProps {
  projectHook: ProjectHookReturn;
}

type Tab = DefineTab;

const KIND_LABELS: Record<StoryElementKind, string> = {
  character: 'Character',
  place: 'Place',
  object: 'Object',
};

const KIND_BADGE_VARIANT: Record<StoryElementKind, 'terra' | 'plum' | 'sage'> = {
  character: 'terra',
  place: 'plum',
  object: 'sage',
};

const TAB_FOR_KIND: Record<StoryElementKind, Tab> = {
  character: 'characters',
  place: 'places',
  object: 'objects',
};

// Format card visual proportions relative to a fixed bounding box
function FormatCardRect({ width, height }: { width: number; height: number }) {
  const boxW = 60;
  const boxH = 48;
  const scale = Math.min(boxW / width, boxH / height) * 0.85;
  const w = Math.round(width * scale);
  const h = Math.round(height * scale);
  return (
    <div
      style={{
        width: boxW,
        height: boxH,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <div
        style={{
          width: w,
          height: h,
          background: 'var(--paper-2)',
          border: '1px solid var(--rule-2)',
          borderRadius: 2,
        }}
      />
    </div>
  );
}

export function DefineStage({ projectHook }: DefineStageProps) {
  // Until the user picks a tab, land on Style for projects that have no
  // art style yet (style conditions every generated image, so it should be
  // decided before the cast is illustrated) and on Characters afterwards.
  // The default is latched on the first render with a loaded project so
  // picking a preset doesn't immediately yank the user off the Style tab.
  const [pickedTab, setPickedTab] = useState<Tab | null>(null);
  const defaultTabRef = useRef<Tab | null>(null);
  if (defaultTabRef.current === null && projectHook.project) {
    defaultTabRef.current = projectHook.project.style_preset ? 'characters' : 'style';
  }
  const activeTab: Tab = pickedTab ?? defaultTabRef.current ?? 'style';
  const setActiveTab = setPickedTab;

  const {
    project,
    storyElements,
    selectedStyle,
    selectedFormat,
    counts,
    currentKind,
    tabElements,
    kindSuggestions,
    pendingSuggestions,
    suggestions,
    extracting,
    deletingRefImage,
    analyzingStyle,
    getRefImages,
    getTraits,
    findElement,
    extract,
    acceptSuggestion,
    rejectSuggestion,
    addElement,
    deleteRefImage,
    selectStyle,
    saveCustomStyle,
    uploadStyleRef,
    reanalyzeStyle,
    selectFormat,
    updateProjectData,
    updateStoryElement,
    deleteStoryElement,
    goToStage,
    continueToCompose,
    refetch,
  } = useDefineStage(projectHook, activeTab);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const [galleryImages, setGalleryImages] = useState<ReferenceImage[]>([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const [refiningRef, setRefiningRef] = useState<{
    image: ReferenceImage;
    ownerType: 'project' | 'story_element';
  } | null>(null);

  const [showNew, setShowNew] = useState(false);
  const [newKind, setNewKind] = useState<StoryElementKind>('character');
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const [customStyleText, setCustomStyleText] = useState(project?.style_prompt_fragment || '');

  const { showToast } = useToast();
  const { status: customStyleSaveStatus } = useAutoSave(customStyleText, saveCustomStyle);
  useEffect(() => {
    if (customStyleSaveStatus === 'error') {
      showToast('Auto-save failed — your latest edit may not be saved', 'error');
    }
  }, [customStyleSaveStatus, showToast]);

  // Sync the textarea with the project's style fragment — needed so Claude's
  // derived style text (written async after a reference upload) shows up here.
  useEffect(() => {
    setCustomStyleText(project?.style_prompt_fragment || '');
  }, [project?.id, project?.style_prompt_fragment]);

  useEffect(() => {
    ensureBookFontsRegistered();
  }, []);

  const openGallery = (images: ReferenceImage[], index: number) => {
    if (images.length === 0) return;
    setGalleryImages(images);
    setGalleryIndex(index);
    setGalleryOpen(true);
  };

  if (!project) return null;

  const isKindTab = (tab: Tab) =>
    tab === 'characters' || tab === 'places' || tab === 'objects';

  const handleAcceptSuggestion = async (index: number) => {
    const kind = await acceptSuggestion(index);
    if (kind) setActiveTab(TAB_FOR_KIND[kind]);
  };

  const handleEditSuggestion = (index: number) => {
    const suggestion = suggestions[index];
    if (!suggestion) return;
    setNewKind(suggestion.kind);
    setNewName(suggestion.name);
    setNewDesc(suggestion.description);
    setShowNew(true);
    rejectSuggestion(index);
  };

  const handleEditElement = (element: StoryElement) => {
    setEditingId(element.id);
  };

  const handleAddElement = async () => {
    const ok = await addElement({ kind: newKind, name: newName, description: newDesc });
    if (ok) {
      setNewKind('character');
      setNewName('');
      setNewDesc('');
      setShowNew(false);
    }
  };

  const editingElement = findElement(editingId);
  const removingElement = findElement(removingId);

  const handleConfirmRemove = async () => {
    if (!removingId) return;
    setRemoveBusy(true);
    const ok = await deleteStoryElement(removingId);
    setRemoveBusy(false);
    if (ok) setRemovingId(null);
  };

  const handleAddForTab = () => {
    if (currentKind) setNewKind(currentKind);
    setShowNew(true);
  };

  const styleRefs = getRefImages('project', project.id);
  // The custom style (typed text and/or reference image) is kept when the
  // author switches to a preset — surface that on the Custom card so they
  // know it's safe to explore presets and come back.
  const hasSavedCustomStyle = !!project.style_prompt_fragment || styleRefs.length > 0;

  return (
    <div className="stage define-stage">
      <div className="stage__header">
        <h2 className="stage__title">Define Your Story</h2>
        <p className="stage__description">
          Set up the look and cast of your book — style and format first, then characters, places, and objects drawn in that style.
        </p>
      </div>

      {/* Toolbar — shown only on element kind tabs for extract/add actions */}
      {isKindTab(activeTab) && (
        <div className="stage-toolbar">
          <div className="stage-toolbar__spacer" />
          <div className="stage-toolbar__group">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void extract()}
              loading={extracting}
            >
              &#9733; Extract from Story
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddForTab}
            >
              + Add {currentKind ? KIND_LABELS[currentKind] : 'Element'}
            </Button>
          </div>
        </div>
      )}

      <div className="define-stage__content">
        {/* 6-tab pill strip. Style and Format lead: both condition every
            image generated from the cast tabs that follow, so they should
            be decided before any portraits are drawn. */}
        <div className="define-tabs">
          <button
            type="button"
            className={`define-tab ${activeTab === 'style' ? 'define-tab--active' : ''}`}
            onClick={() => setActiveTab('style')}
          >
            Illustration style
          </button>
          <button
            type="button"
            className={`define-tab ${activeTab === 'format' ? 'define-tab--active' : ''}`}
            onClick={() => setActiveTab('format')}
          >
            Format
          </button>
          <button
            type="button"
            className={`define-tab ${activeTab === 'characters' ? 'define-tab--active' : ''}`}
            onClick={() => setActiveTab('characters')}
          >
            Characters
            <span className="define-tab__count">{counts.character}</span>
          </button>
          <button
            type="button"
            className={`define-tab ${activeTab === 'places' ? 'define-tab--active' : ''}`}
            onClick={() => setActiveTab('places')}
          >
            Places
            <span className="define-tab__count">{counts.place}</span>
          </button>
          <button
            type="button"
            className={`define-tab ${activeTab === 'objects' ? 'define-tab--active' : ''}`}
            onClick={() => setActiveTab('objects')}
          >
            Objects
            <span className="define-tab__count">{counts.object}</span>
          </button>
          <button
            type="button"
            className={`define-tab ${activeTab === 'typography' ? 'define-tab--active' : ''}`}
            onClick={() => setActiveTab('typography')}
          >
            Typography
          </button>
        </div>

        {/* CHARACTERS / PLACES / OBJECTS — cast-card grid */}
        {isKindTab(activeTab) && (
          <div className="define-stage__panel">
            {/* All-kinds pending suggestions (shown on the relevant kind tab) */}
            {kindSuggestions.length > 0 && (
              <div className="define-stage__suggestions">
                {suggestions.map((suggestion, index) =>
                  suggestion.status === 'pending' && suggestion.kind === currentKind ? (
                    <AISuggestion
                      key={index}
                      title={`${KIND_LABELS[suggestion.kind]}: ${suggestion.name}`}
                      onAccept={() => handleAcceptSuggestion(index)}
                      onReject={() => rejectSuggestion(index)}
                      onEdit={() => handleEditSuggestion(index)}
                    >
                      <p>
                        <Badge variant={KIND_BADGE_VARIANT[suggestion.kind]}>
                          {KIND_LABELS[suggestion.kind]}
                        </Badge>{' '}
                        <strong>{suggestion.name}</strong>
                      </p>
                      <p>{suggestion.description}</p>
                    </AISuggestion>
                  ) : null,
                )}
              </div>
            )}

            {/* New element form */}
            {showNew && (
              <Card className="define-stage__new-form" padding="md">
                <h4>New {currentKind ? KIND_LABELS[currentKind] : 'Story Element'}</h4>
                <label className="define-stage__kind-field">
                  <span>Kind</span>
                  <select
                    value={newKind}
                    onChange={(e) => setNewKind(e.target.value as StoryElementKind)}
                  >
                    <option value="character">Character</option>
                    <option value="place">Place</option>
                    <option value="object">Object</option>
                  </select>
                </label>
                <Input
                  label="Name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Rosie, the Whispering Woods, the golden key"
                  autoFocus
                />
                <Textarea
                  label="Description"
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="Brief description..."
                />
                <div className="define-stage__form-actions">
                  <Button variant="ghost" size="sm" onClick={() => setShowNew(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleAddElement}>
                    Add
                  </Button>
                </div>
              </Card>
            )}

            {/* Cast-card grid */}
            <div className="cast-grid">
              {tabElements.map((element) => {
                const refImages = getRefImages('story_element', element.id);
                const portrait = refImages[refImages.length - 1] ?? null;
                const traits = getTraits(element.visual_description);
                return (
                  <div key={element.id} className="cast-card-wrap">
                    <button
                      type="button"
                      className="cast-card"
                      onClick={() => handleEditElement(element)}
                    >
                      <div className="cast-card__portrait">
                        {portrait ? (
                          <img
                            src={r2Url(portrait.r2_key)}
                            alt={element.name}
                            className="cast-card__portrait-img"
                          />
                        ) : (
                          <div className="cast-card__portrait-placeholder">
                            {element.kind === 'character' ? '👤' : element.kind === 'place' ? '🏕️' : '📦'}
                          </div>
                        )}
                        {portrait?.stale && (
                          <span
                            className="cast-card__stale"
                            title={portrait.stale_reason ?? 'Changed since this was drawn'}
                          >
                            stale
                          </span>
                        )}
                      </div>
                      <div className="cast-card__name">{element.name}</div>
                      {element.description && (
                        <div className="cast-card__sub">{element.description}</div>
                      )}
                      {traits.length > 0 && (
                        <div className="cast-card__traits">
                          {traits.map((trait, i) => (
                            <span key={i} className="cast-card__trait">{trait}</span>
                          ))}
                        </div>
                      )}
                    </button>
                    <button
                      type="button"
                      className="cast-card__remove"
                      aria-label={`Remove ${element.name}`}
                      title={`Remove this ${KIND_LABELS[element.kind].toLowerCase()}`}
                      onClick={() => setRemovingId(element.id)}
                    >
                      &times;
                    </button>
                  </div>
                );
              })}

              {/* Ghost "add" card */}
              <button
                type="button"
                className="cast-card cast-card--ghost"
                onClick={handleAddForTab}
              >
                <div>
                  <div style={{ fontSize: 24, marginBottom: 6 }}>+</div>
                  <div>Add {currentKind ? KIND_LABELS[currentKind] : 'Element'}</div>
                </div>
              </button>
            </div>

            {tabElements.length === 0 && kindSuggestions.length === 0 && !showNew && (
              <p className="define-stage__empty">
                No {currentKind}s yet. Use "Extract from Story" or add them manually.
              </p>
            )}
          </div>
        )}

        {/* ILLUSTRATION STYLE TAB */}
        {activeTab === 'style' && (
          <div className="define-stage__panel">
            <div className="section" style={{ marginTop: 0 }}>
              <h3 className="define-stage__section-title">Illustration style</h3>
              <p className="define-stage__section-sub">
                Pick a base style, or upload a reference image and we'll extract the style automatically.
              </p>
              <div className="define-stage__style-grid">
                {STYLE_PRESETS.map((preset) => (
                  <div
                    key={preset.value}
                    className={`define-stage__style-card ${selectedStyle === preset.value ? 'define-stage__style-card--selected' : ''}`}
                    onClick={() => selectStyle(preset.value)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && selectStyle(preset.value)}
                  >
                    <div className="define-stage__style-card-body">
                      <h4 className="define-stage__style-label">
                        {preset.label}
                        {preset.value === 'custom' &&
                          selectedStyle !== 'custom' &&
                          hasSavedCustomStyle && (
                            <Badge variant="sage">saved</Badge>
                          )}
                      </h4>
                      <p className="define-stage__style-desc">{preset.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {selectedStyle === 'custom' && (
                <Textarea
                  label="Custom Style Description"
                  value={customStyleText}
                  onChange={(e) => setCustomStyleText(e.target.value)}
                  placeholder="Describe your desired art style..."
                  helper={
                    styleRefs.length > 0
                      ? 'Derived from your reference image — edit freely, or re-analyze to start over from the image.'
                      : undefined
                  }
                  className="define-stage__custom-style"
                  variant="ui"
                />
              )}
            </div>

            {selectedStyle === 'custom' && (
              <div className="section">
                <h3 className="define-stage__section-title">Style Reference Images</h3>
                {analyzingStyle && (
                  <p className="define-stage__section-sub">Analysing style…</p>
                )}
                <ImageEditorList
                  ownerType="project"
                  ownerId={project.id}
                  projectId={project.id}
                  images={styleRefs}
                  itemLabel="style reference"
                  features={{
                    refine: true,
                    gallery: true,
                    delete: true,
                  }}
                  onRefine={(img) =>
                    setRefiningRef({ image: img, ownerType: 'project' })
                  }
                  onClickImage={(_img, idx) => openGallery(styleRefs, idx)}
                  onDelete={(img) => deleteRefImage(img.id)}
                  deletingIds={
                    deletingRefImage ? new Set([deletingRefImage]) : undefined
                  }
                  onUpload={styleRefs.length >= 1 ? undefined : uploadStyleRef}
                  uploadLabel="Upload a style reference"
                  emptyMessage="Upload an image in the style you want — we'll analyse it and use the visual style across all pages."
                />
                {styleRefs.length > 0 && (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={analyzingStyle}
                    onClick={() => void reanalyzeStyle()}
                  >
                    &#9733; Re-analyze style from image
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* TYPOGRAPHY TAB — text placement is freeform on Compose, so only
            the book's typeface settings live here. */}
        {activeTab === 'typography' && (
          <div className="define-stage__panel">
            <Card padding="lg" className="define-stage__settings-card">
              <TypographySettings
                project={project}
                onUpdate={(data: Partial<UpdateProjectRequest>) => updateProjectData(data)}
              />
            </Card>
          </div>
        )}

        {/* FORMAT TAB */}
        {activeTab === 'format' && (
          <div className="define-stage__panel">
            <h3 className="define-stage__section-title">Book format</h3>
            <p className="define-stage__section-sub">
              Final printed size. You can change this any time before exporting.
            </p>
            <div className="define-stage__format-grid">
              {FORMAT_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  className={`define-stage__format-card ${selectedFormat === preset.value ? 'define-stage__format-card--selected' : ''}`}
                  onClick={() => selectFormat(preset.value as FormatPreset)}
                >
                  <div className="define-stage__format-preview">
                    <FormatCardRect width={preset.width} height={preset.height} />
                  </div>
                  <div className="define-stage__format-name">{preset.label}</div>
                  <div className="define-stage__format-dim">{preset.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="stage__footer">
        <Button
          variant="ghost"
          onClick={() => goToStage('capture')}
        >
          &larr; Back to Capture
        </Button>
        <Button onClick={() => void continueToCompose()}>
          Continue to Compose &rarr;
        </Button>
      </div>

      {/* Element editor modal */}
      {editingElement && (
        <CastEditorModal
          open={!!editingElement}
          element={editingElement}
          project={project}
          updateStoryElement={updateStoryElement}
          deleteStoryElement={deleteStoryElement}
          refetch={refetch}
          onClose={() => setEditingId(null)}
          onGoToStyle={() => setActiveTab('style')}
        />
      )}

      {/* Remove-element confirmation (from the card grid's × button) */}
      {removingElement && (
        <ConfirmDialog
          open={true}
          title={`Delete ${KIND_LABELS[removingElement.kind].toLowerCase()}?`}
          message={`"${removingElement.name}" and its reference images will be removed from this story.`}
          confirmLabel="Delete"
          loading={removeBusy}
          onConfirm={() => void handleConfirmRemove()}
          onCancel={() => setRemovingId(null)}
        />
      )}

      <ImageGallery
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        images={galleryImages}
        initialIndex={galleryIndex}
      />

      {refiningRef && (
        <RefineImageModal
          open={true}
          project={project}
          imageRef={{
            kind: 'reference_image',
            project_id: project.id,
            owner_type: refiningRef.ownerType,
            owner_id: refiningRef.image.owner_id,
            reference_image_id: refiningRef.image.id,
          }}
          currentR2Key={refiningRef.image.r2_key}
          targetLabel={
            refiningRef.ownerType === 'project'
              ? 'style reference'
              : `${
                  storyElements.find((e) => e.id === refiningRef.image.owner_id)?.name ??
                  'element'
                } reference`
          }
          onClose={() => setRefiningRef(null)}
          onAccepted={() => {
            void refetch();
          }}
        />
      )}

      {/* Unused pending suggestions for non-active kind tabs get shown at top level */}
      {pendingSuggestions.length > 0 && !isKindTab(activeTab) && (
        <div
          style={{
            position: 'fixed',
            bottom: 80,
            right: 24,
            zIndex: 100,
            background: 'var(--paper-0)',
            border: '1px solid var(--rule-1)',
            borderRadius: 'var(--r-lg)',
            padding: '10px 14px',
            fontSize: 13,
            color: 'var(--ink-2)',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          {pendingSuggestions.length} suggestion{pendingSuggestions.length !== 1 ? 's' : ''} pending —{' '}
          <button
            type="button"
            style={{ color: 'var(--terra)', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            onClick={() => setActiveTab('characters')}
          >
            view
          </button>
        </div>
      )}
    </div>
  );
}
